import { action, computed, observable, makeObservable } from 'mobx';
import { ICycleConfiguration } from 'typings/cycleConfiguration';
import { dataManagerFactory, updateConfiguration,navigateToNoMst } from 'utils/MobxDataManager/singletons';
import { ConfigurationUtils } from 'utils/configurationsUtils';
import {
    CycleStatus,
    EventTypes,
    EntityTypes,
    PayRangeDisplayType,
    CompaRatioDisplayFormat,
    BaseOfCalculation,
    DataState,
    ConfigTabs,
    ConfigViews,
    EmployeeLevelEnum
} from 'typings/enums';
import { EmployeeConfiguration } from 'nomst/Stores/Configuration/employeeConfiguration';
import { IConfigurationDataManager, IEmployeeDataManager, ITeamCycleManager } from 'typings/dataManagers';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { IInternalPayRangeConfiguration } from 'typings/interalPayRangeConfiguration';
import { ConfigurationFactory } from 'nomst/Stores/Configuration/configurationFactory';
import { EventStore } from 'nomst/Stores/Decision/event.store';
import { INotificationDisabledUser } from 'typings/MobxComponents/notificationDisabledUser';
import { IWorkflowConfiguration } from 'typings/workflowConfiguration';
import { BaseEmployeeLevel } from 'nomst/Stores/Configuration/baseEmployeeLevel';
import { AssignUserModalView } from 'nomst/Stores/Configuration/assignUserModalView';
import { SettingsConfiguration } from 'nomst/Stores/Configuration/settingsConfiguration';
import { IConfiguration } from 'typings/configuration';
import { ICycleNavigation } from 'typings/cycleNavigation';
import { IBaseEmployeeLevel } from 'typings/baseEmployeeLevel';

export class ConfigurationsStore {
    @observable _currentCycleConfiguration: Nullable<ICycleConfiguration>;
    _employeeConfiguration: EmployeeConfiguration;
    _configuration: SettingsConfiguration;
    _masterAccountId: string;
    _configurationUtils: ConfigurationUtils;
    _configurationDataManager: IConfigurationDataManager;
    _employeeDataManager: IEmployeeDataManager;
    _teamCycleManager: ITeamCycleManager;
    _userId: string;
    _eventStore: EventStore;
    @observable currentConfigView: ConfigViews;
    @observable assignUserHrReviewer: AssignUserModalView;
    @observable _cycleSettingsId: number;
    @observable _convertedConfiguration: Map<number, Configuration>;
    @observable private _cycleConfigurations: ICycleConfiguration[];
    private _accessToken: string;
    @observable private _notificationDisabledUsers: Nullable<INotificationDisabledUser[]>;
    @observable employeeLevels: BaseEmployeeLevel[];
    @observable configurationTab: ConfigTabs;

    constructor(masterAccountId: string, userId: string, accessToken: string) {
        makeObservable(this);
        this._masterAccountId = masterAccountId;
        this._userId = userId;
        this._accessToken = accessToken;
        this._configurationUtils = new ConfigurationUtils();
        this._configurationDataManager = dataManagerFactory.getConfigurationDataManager();
        this._employeeDataManager = dataManagerFactory.getEmployeeDataManager();
        this._teamCycleManager = dataManagerFactory.getTeamCycleManager();
        this._cycleConfigurations = [];
        this._convertedConfiguration = new Map<number, Configuration>();
        this._eventStore = new EventStore();
        this._notificationDisabledUsers = [];
        this.employeeLevels = [];
        this.assignUserHrReviewer = new AssignUserModalView(this._masterAccountId, this.handleSaveHrReviewerUserCallback.bind(this));
    }

    @action
    setCurrentConfigViews(view: ConfigViews): void {
        this.currentConfigView = view;
    }

    @action
    setConfigurationTab(view: ConfigTabs): void {
        this.configurationTab = view;
    }

    @action
    clearCycleconfigurations(): void {
        this._cycleConfigurations = [];
    }

    @action
    unsetAllSelections(): void {
        this.employeeLevels.forEach((employeeLevel: BaseEmployeeLevel) => {
            employeeLevel.setSelection(false);
        });
    }

    @action
    removeHrReviewerUser(levelName: string, userId: string): void {
        const removedEmployeeLevel = this.employeeLevels.find((employeeLevel: BaseEmployeeLevel) =>
            employeeLevel.hrReviewer.selectedUserId === userId &&
        employeeLevel.levelName === levelName);

        if (removedEmployeeLevel) {
            removedEmployeeLevel.hrReviewer.remove();
        }
    }

    @action
    toggleSelectionsForLevel(level: string): void {
        const newSelection = !this.anySelectedGroup;
        this.employeeLevels.forEach((employeeLevel: BaseEmployeeLevel) => {
            if (employeeLevel.level === level) {
                employeeLevel.setSelection(newSelection);
            }
        });
    }

    navigateConfigurationView(): void {
        return navigateToNoMst.navigateToConfigurationView(this.configurationTab);
    }

    navigateCycleSettingsView(nav: ICycleNavigation): void {
        return navigateToNoMst.navigateCycleConfigurationView(nav);
    }

    @action
    removeClosedCycleConfiguration(): void {
        if (!this._cycleConfigurations) {
            return;
        }

        const closedCycle = this._cycleConfigurations.find(
            config => config.status === CycleStatus.Closed || config.status === CycleStatus.Published);
        if (closedCycle) {
            this._convertedConfiguration.delete(closedCycle.cycleId);
            this._cycleConfigurations = this._cycleConfigurations.filter(
                config => config.status !== CycleStatus.Closed && config.status !== CycleStatus.Published);
        }
    }

    @computed
    get anySelectedGroup(): boolean {
        return this.employeeLevels
            .find((employeeLevel: BaseEmployeeLevel) => employeeLevel.isSelected) !== undefined;
    }

    @computed
    get cycleConfigurations(): ICycleConfiguration[] {
        return this._cycleConfigurations;
    }

    @computed
    get allCreatedCyclePayEffectiveDates(): Date[] {
        return this._cycleConfigurations.map(config => {
            return new Date(config.payEffectiveDate);
        });
    }

    get masterAccountId(): string {
        return this._masterAccountId;
    }

    @computed
    get notificationDisabledUsers(): Nullable<INotificationDisabledUser[]> {
        return this._notificationDisabledUsers;
    }

    get anyOpenCycleConfigurations(): boolean {
        if (!this._cycleConfigurations) {
            return false;
        }

        return this._cycleConfigurations.find(cycleConfiguration =>
            cycleConfiguration.status === CycleStatus.Open) !== undefined;
    }

    @computed
    get anyClosedUnpublishedCycles(): boolean {
        if (!this._cycleConfigurations) {
            return false;
        }

        return this._cycleConfigurations.find(cycleConfiguration =>
            cycleConfiguration.status === CycleStatus.Closed) !== undefined;
    }

    @computed
    get convertedConfigurations(): Configuration[] {
        return Array.from(this._convertedConfiguration.values());
    }

    @action
    resetConvertedConfigurations(): void {
        this._convertedConfiguration = new Map<number, Configuration>();
        this._cycleConfigurations.forEach(config => this.addConfigurationToMap(config));
    }

    @action
    async fetchCycleConfigurations(): Promise<void> {
        if (this._cycleConfigurations.length) {
            return;
        }
        if (!this._configuration) {
            await this.fetchConfiguration();
        }
        this._cycleConfigurations = await this._configurationDataManager.getCycleConfigurations(this._masterAccountId);
        this._cycleConfigurations.forEach(cycleConfiguration =>
            this._configurationUtils.bindFieldsToBudgets(cycleConfiguration));
        this.resetConvertedConfigurations();
        await this.fetchDisableEmailNotifications();
    }

    @action
    async fetchConfiguration(): Promise<void> {
        const employeeConfiguration  = await this._employeeDataManager.getConfiguration(this._masterAccountId);
        this._configuration = new SettingsConfiguration(await this._configurationDataManager.getConfiguration(this._masterAccountId));
        this._employeeConfiguration = new EmployeeConfiguration(employeeConfiguration.formatConfiguration);
    }

    @action
    async fetchCurrentCycleConfiguration(): Promise<void> {
        this._currentCycleConfiguration =
            await this._configurationDataManager.getCurrentCycleConfiguration(this._masterAccountId);

        if (this._currentCycleConfiguration) {
            this._currentCycleConfiguration.budgets
                .forEach(budget => this._configurationUtils
                    .bindAmountsToBudget(budget, this._currentCycleConfiguration?.budgetFields || []));
        }
        if (!this._configuration) {
            await this.fetchConfiguration();
        }
        this.addConfigurationToMap(this._currentCycleConfiguration);
    }

    @action
    addConfigurationToMap(cycleConfiguration: Nullable<ICycleConfiguration>): void {
        if (!cycleConfiguration) {
            return;
        }

        const newConfiguration = ConfigurationFactory.getConfiguration(
            this._masterAccountId,
            cycleConfiguration.cycleId,
            this._configuration,
            this._employeeConfiguration.formatConfiguration,
            cycleConfiguration,
            this._accessToken,
            this._userId
        );

        this._convertedConfiguration.set(cycleConfiguration.cycleId, newConfiguration);
    }

    @action
    addConvertedConfigurationToMap(config: Configuration): void {
        this._convertedConfiguration.set(config.cycleId, config);
    }

    @computed
    get currentConvertedConfiguration(): Configuration {
        if (!this._currentCycleConfiguration) {
            return { cycleId: 0 } as Configuration;
        }

        return this._convertedConfiguration.get(this._currentCycleConfiguration.cycleId)
            || { cycleId: 0 } as Configuration;
    }

    @action
    async saveCycleConfiguration(newCycleConfiguration?: Nullable<ICycleConfiguration>):
        Promise<Nullable<ICycleConfiguration>> {
        if (!newCycleConfiguration) {
            return null;
        }
        let savedCycleConfiguration: Nullable<ICycleConfiguration> = null;
        const savedCycleId =
            await this._configurationDataManager
                .saveCycleConfiguration(this._masterAccountId, newCycleConfiguration.cycleId, newCycleConfiguration);

        await this.fetchCurrentCycleConfiguration();

        if (savedCycleId) {
            savedCycleConfiguration =
                await this._configurationDataManager.getSpecificCycleConfiguration(this._masterAccountId, savedCycleId);
            if (savedCycleConfiguration) {
                this._configurationUtils.bindFieldsToBudgets(savedCycleConfiguration);
            }

            this.addConfigurationToMap(savedCycleConfiguration);
        }
        return savedCycleConfiguration;
    }

    async saveConfiguration(): Promise<void> {
        await Promise.all([
            this._employeeDataManager.saveConfiguration(this.masterAccountId, this._employeeConfiguration),
            this._configurationDataManager.saveConfiguration(this.masterAccountId, this._configuration as unknown as IConfiguration)
        ]);
    }

    async setCycleConfigurationStatusToOpen(): Promise<Nullable<ICycleConfiguration>> {
        if (!this.cycleSettingsViewCycle) {
            return null;
        }

        this.cycleSettingsViewCycle.previewMode = true;

        const savedCycleConfiguration = await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
        if (!savedCycleConfiguration) {
            return null;
        }

        await this._configurationDataManager.updateCycleConfigurationStatus(
            this._masterAccountId,
            savedCycleConfiguration.cycleId,
            CycleStatus.Open);

        await this.fetchCurrentCycleConfiguration();

        this.cycleSettingsViewCycle.status = CycleStatus.Open;
        this.addConfigurationToMap(this.cycleSettingsViewCycle);
        this.removeClosedCycleConfiguration();

        return savedCycleConfiguration;
    }

    @action
    async setCycleConfigurationStatusToPublished(): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        await this._configurationDataManager.updateCycleConfigurationStatus(
            this._masterAccountId,
            this.cycleSettingsViewCycle.cycleId,
            CycleStatus.Published
        );

        this.cycleSettingsViewCycle.status = CycleStatus.Published;
        this.addConfigurationToMap(this.cycleSettingsViewCycle);
    }

    @action
    async createCycleConfiguration(newCycleConfiguration: ICycleConfiguration): Promise<void> {
        const savedCycleId =
            await this._configurationDataManager.createCycleConfiguration(this._masterAccountId, newCycleConfiguration);

        await this.fetchCurrentCycleConfiguration();
        let savedCycleConfiguration: Nullable<ICycleConfiguration> = null;
        if (savedCycleId) {
            savedCycleConfiguration =
                await this._configurationDataManager.getSpecificCycleConfiguration(this._masterAccountId, savedCycleId);
        }
        if (savedCycleConfiguration) {
            this._cycleConfigurations.push(savedCycleConfiguration);
            this.resetConvertedConfigurations();
            this._cycleSettingsId = savedCycleConfiguration.cycleId;
        }

    }

    @action
    async deleteCycleConfiguration(cycleConfiguration: ICycleConfiguration): Promise<void> {
        if (cycleConfiguration.cycleId > 0) {
            await this._configurationDataManager.deleteCycleConfiguration(
                this._masterAccountId, cycleConfiguration.cycleId);
            this._currentCycleConfiguration = null;
            this._cycleConfigurations = this._cycleConfigurations.filter(config => {
                return config.cycleId !== cycleConfiguration.cycleId;
            }) as ICycleConfiguration[];
            this.resetConvertedConfigurations();
        }
    }

    @action
    async resetCycleConfiguration(): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this._configurationDataManager.setDataState(DataState.Pending);
        const newCycleId = await this._configurationDataManager.resetCycleConfiguration(
            this._masterAccountId,
            this.cycleSettingsViewCycle.cycleId
        );

        if (newCycleId) {
            this._currentCycleConfiguration = null;
            this._cycleConfigurations = [];
            await Promise.all([this.fetchCycleConfigurations(), this.fetchCurrentCycleConfiguration()]);
            this.setConvertedCycleSettingsViewCycle(newCycleId);
            updateConfiguration();
        }

        this._configurationDataManager.setDataState(DataState.Idle);
    }

    async saveWorkflowConfiguration(workflowConfig: IWorkflowConfiguration): Promise<void> {
        if (!this._cycleSettingsId) {
            return;
        }

        return this._configurationDataManager.saveWorkflowConfiguration(
            this._masterAccountId, this._cycleSettingsId, workflowConfig);
    }

    @action
    async getHrReviewers(): Promise<void> {
        this.employeeLevels = [];
        const [
            employeeLevelDepartments,
            employeeLevelBusinessUnit,
            employeeLevelDivision
        ] =
            await Promise.all([
                this._employeeDataManager.getEmployeeDepartments(this.masterAccountId),
                this._employeeDataManager.getEmployeeBusinessUnits(this.masterAccountId),
                this._employeeDataManager.getEmployeeDivisions(this.masterAccountId)
            ]);

        if (employeeLevelDepartments && employeeLevelDepartments.length > 0) {
            employeeLevelDepartments.forEach(department => this.employeeLevels.push(new BaseEmployeeLevel(
                {
                    level: EmployeeLevelEnum.Department,
                    levelName: department
                } as IBaseEmployeeLevel,
                this.masterAccountId)));
        }

        if (employeeLevelBusinessUnit && employeeLevelBusinessUnit.length > 0) {
            employeeLevelBusinessUnit.forEach(level => this.employeeLevels.push(new BaseEmployeeLevel(level, this.masterAccountId)));
        }

        if (employeeLevelDivision && employeeLevelDivision.length > 0) {
            employeeLevelDivision.forEach(level => this.employeeLevels.push(new BaseEmployeeLevel(level, this.masterAccountId)));
        }

        const reviewerUsers: IHrReviewerUser[] =
            (await this._configurationDataManager.getHrReviewerUsers(this.masterAccountId)) ?? [];

        this.employeeLevels.forEach((employeeLevel: BaseEmployeeLevel) => {
            const reviewer =
                reviewerUsers.find(reviewerUser => reviewerUser.level === employeeLevel.level &&
                    reviewerUser.levelName === employeeLevel.levelName);
            if (!reviewer) {
                return;
            }

            employeeLevel.hrReviewer.setUserId(reviewer.userId);
            employeeLevel.hrReviewer.setWorkEmail(reviewer.workEmail);
            employeeLevel.hrReviewer.setDisplayName(reviewer.displayName);
        });
    }

    @action
    getDefaultSelectedCycleConfig(): Nullable<ICycleConfiguration> {
        const currentCycleId = this._currentCycleConfiguration ? this._currentCycleConfiguration.cycleId : null;
        const currentCycle = this._cycleConfigurations.find(config => config.cycleId === currentCycleId);

        if (currentCycle) {
            return currentCycle;
        }

        if (this._cycleConfigurations.length > 0) {
            return this._cycleConfigurations[0];
        }

        return null;
    }

    getSpecificConfiguration(id: number): Configuration {
        return this._convertedConfiguration.get(id) || {} as Configuration;
    }

    @computed
    get convertedCycleSettingsViewCycle(): Configuration {
        return this._convertedConfiguration.get(this._cycleSettingsId) || {} as Configuration;
    }

    @computed
    get cycleSettingsViewCycle(): Nullable<ICycleConfiguration> {
        return this._cycleConfigurations.find(cycleConfig => cycleConfig.cycleId === this._cycleSettingsId) || null;
    }

    @computed
    get newCycleConfiguration(): ICycleConfiguration {
        // set default objects the server expects
        const teamPageViews = {
            excludedCompSummaryFields: [],
            excludedTccFields: [],
            increaseRecommendation: true,
            lockViewForManager: false,
            managerIncreaseDecision: false,
            managerIncreasePercentageUnder: 0,
            managerIncreasePercentageOver: 0,
            multiCountry: false,
            preFillDecision: false,
            preFillDecisionFields: [],
            readOnlyView: false,
            shouldDisplayAllCompSummaryFields: true,
            tcc: true,
            hideIneligibleEmployees: false,
            disableCompMemoDownload: false
        } as ITeamPageViews;
        const internalPayRange = {
            baseOfCalculation: BaseOfCalculation.BasePay,
            compaRatioDisplayFormat: CompaRatioDisplayFormat.ShowAsPercentage,
            displayType: PayRangeDisplayType.RangePenetration,
            showPayRange: true
        } as IInternalPayRangeConfiguration;
        const ratingConfiguration = {
            allowEditPerformanceRating: false,
            showPerformanceRating: true
        } as IRatingConfiguration;
        return { payEffectiveDate: '', endDate: '', ratingConfiguration,
            teamPageViews, internalPayRange } as ICycleConfiguration;
    }

    @action
    setConvertedCycleSettingsViewCycle(cycleId: number): void {
        if (!cycleId) {
            this._currentCycleConfiguration = this.getDefaultSelectedCycleConfig();
            return;
        }

        const cycleConfig = this._cycleConfigurations.find(item => item.cycleId === cycleId);
        if (cycleConfig) {
            this._cycleSettingsId = cycleId;
        }
    }

    // for testing purposes
    @action
    addCycleConfigurationTest(config: ICycleConfiguration): void {
        this._cycleConfigurations.push(config);
    }

    @action
    async approveClosePlan(blockUi: boolean): Promise<void> {
        const teamCycleId = this.cycleSettingsId;
        const event = {
            eventType: EventTypes.TeamCloseCycleEvent,
            entityType: EntityTypes.TeamCycle,
            userId: this._userId,
            data: {
                cycleId: teamCycleId,
                closedByUserId: this._userId
            }
        };

        await this._teamCycleManager.closePlan(
            this._masterAccountId,
            teamCycleId,
            event,
            blockUi);

        if (this.cycleSettingsViewCycle) {
            this.cycleSettingsViewCycle.status = CycleStatus.Closing;
            this.addConfigurationToMap(this.cycleSettingsViewCycle);
        }
    }

    get currentCycleConfiguration(): Nullable<ICycleConfiguration> {
        return this._currentCycleConfiguration;
    }

    get currentCycleId(): number {
        if (!this._currentCycleConfiguration) {
            return 0;
        }
        return this._currentCycleConfiguration.cycleId;
    }

    get cycleSettingsId(): number {
        if (!this._cycleSettingsId) {
            this._cycleSettingsId = this.getDefaultSelectedCycleConfig()?.cycleId ?? 0;
        }
        return this._cycleSettingsId;
    }

    @action
    async toggleShowPeformanceRating(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.ratingConfiguration.showPerformanceRating = value;
        if (!value) {
            this.cycleSettingsViewCycle.ratingConfiguration.allowEditPerformanceRating = value;
        }
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async togglePeformanceRatingEdit(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.ratingConfiguration.allowEditPerformanceRating = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async toggleUseCompSummaryFields(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.shouldDisplayAllCompSummaryFields = value;
        if (!value) {
            this.cycleSettingsViewCycle.teamPageViews.excludedCompSummaryFields = [];
        }

        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async toggleAllowCompMemoDownload(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.disableCompMemoDownload = !!value;

        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async toggleHideIneligibleEmployees(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.hideIneligibleEmployees = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async toggleShowPayRange(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.internalPayRange.showPayRange = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setExcludedCompSummaryFields(fields: string[]): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.excludedCompSummaryFields = fields;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setPayRangeDisplayType(value: PayRangeDisplayType): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.internalPayRange.displayType = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async toggleShowIncreaseRecommendation(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.increaseRecommendation = value;
        if (!value) {
            this.cycleSettingsViewCycle.teamPageViews.managerIncreaseDecision = false;
            this.cycleSettingsViewCycle.teamPageViews.managerIncreasePercentageUnder = 0;
            this.cycleSettingsViewCycle.teamPageViews.managerIncreasePercentageOver = 0;
        }
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setCompRatioDisplayFormat(value: CompaRatioDisplayFormat): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.internalPayRange.compaRatioDisplayFormat = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async toggleManagerIncreaseDecision(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.managerIncreaseDecision = value;
        if (!value) {
            this.cycleSettingsViewCycle.teamPageViews.managerIncreasePercentageUnder = 0;
            this.cycleSettingsViewCycle.teamPageViews.managerIncreasePercentageOver = 0;
        }
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setBaseOfCalculation(value: BaseOfCalculation): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.internalPayRange.baseOfCalculation = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setManagerIncreasePercentageUnder(value: number): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.managerIncreasePercentageUnder = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setManagerIncreasePercentageOver(value: number): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.managerIncreasePercentageOver = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setPreFillDecision(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        this.cycleSettingsViewCycle.teamPageViews.preFillDecision = value;
        if (!value) {
            this.cycleSettingsViewCycle.teamPageViews.preFillDecisionFields = [];
        }

        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setPreFillDecisionFields(values: string[]): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        const preFillFields = this.cycleSettingsViewCycle.budgetFields
            .filter(field => !!values.find(value => value === field.title))
            .map(field => field.amountCode.toString());
        this.cycleSettingsViewCycle.teamPageViews.preFillDecisionFields = preFillFields;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    async handleSaveHrReviewerUserCallback(userId: string, workEmail: string, displayName: string): Promise<void> {
        const employeeLevels = this.employeeLevels
            .filter((employeeLevel: BaseEmployeeLevel) => employeeLevel.isSelected);
        employeeLevels.forEach((employeeLevel: BaseEmployeeLevel) => {
            employeeLevel.hrReviewer.setUserId(userId);
            employeeLevel.hrReviewer.setWorkEmail(workEmail);
            employeeLevel.hrReviewer.setDisplayName(displayName);
        });

        await this._configurationDataManager
            .saveHrReviewerUser(this.masterAccountId, userId, employeeLevels);
    }

    @action
    async handleNotifyManagersCall(): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }

        // Update configuration to unlock UI for managers
        this.cycleSettingsViewCycle.previewMode = false;

        // Save configuration and emit event to send welcome email to managers
        await Promise.all([
            this.saveCycleConfiguration(this.cycleSettingsViewCycle),
            this._eventStore.emitNotifyManagersEvent(
                this._masterAccountId,
                this.cycleSettingsViewCycle.cycleId,
                this._userId
            )]);
    }

    @action
    async setTccDisplayValue(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.teamPageViews.tcc = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async addRemoveFieldsForTccCalc(value: string[]): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.teamPageViews.excludedTccFields = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async toggleLockViewForManagers(value: boolean): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.teamPageViews.lockViewForManager = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setCycleEndDate(value: string): Promise<void>  {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.endDate = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setCyclePayEffectiveDate(value: string): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.payEffectiveDate = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async setConfigurationComment(value: string): Promise<void> {
        if (!this.cycleSettingsViewCycle) {
            return;
        }
        this.cycleSettingsViewCycle.comment = value;
        await this.saveCycleConfiguration(this.cycleSettingsViewCycle);
    }

    @action
    async disableEmailNotification(matchedEmail: string): Promise<void> {
        await this._configurationDataManager.updateNotifications(
            this._masterAccountId,
            matchedEmail,
            false);
        await this.fetchDisableEmailNotifications();
    }

    @action
    async enableEmailNotification(email: string): Promise<void> {
        await this._configurationDataManager.updateNotifications(
            this._masterAccountId,
            email,
            true);
        await this.fetchDisableEmailNotifications();
    }

    async getSuggestedEmails(
        searchText: string,
        resultLimit: number): Promise<Nullable<string[]>> {
        return await this._configurationDataManager.getSuggestedEmails(
            this._masterAccountId,
            searchText,
            resultLimit);
    }

    @action
    async fetchDisableEmailNotifications(): Promise<void> {
        this._notificationDisabledUsers = await this._configurationDataManager
            .getNotificationDisabledUsers(this._masterAccountId);
    }
}